import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { Camera, MapPin, Train, ExternalLink, Radio, Route as RouteIcon, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTripCart } from "@/lib/tripCart";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Fix default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Pin-shaped marker for depots
const createDepotIcon = (color) =>
  L.divIcon({
    className: "custom-marker depot-marker",
    html: `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
        <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24c0-6.627-5.373-12-12-12z"/>
        <circle fill="#fff" cx="12" cy="12" r="5"/>
      </svg>
    `,
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });

// Diamond + "T" marker for towers (visually distinct from depot pins)
const createTowerIcon = (color) =>
  L.divIcon({
    className: "custom-marker tower-marker",
    html: `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
        <path fill="${color}" stroke="#fff" stroke-width="2" d="M14 1 L27 14 L14 27 L1 14 Z"/>
        <text x="14" y="19" text-anchor="middle" font-family="ui-monospace,monospace" font-size="13" font-weight="900" fill="#fff">T</text>
      </svg>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });

const getDepotIcon = (depot) => {
  if (depot.status === "original") {
    return depot.has_photo ? createDepotIcon("#22c55e") : createDepotIcon("#ef4444");
  }
  return depot.has_photo ? createDepotIcon("#3b82f6") : createDepotIcon("#eab308");
};

const getTowerIcon = (tower) => {
  // amber for preserved/active, slate for demolished
  if (tower.status === "active") return createTowerIcon("#a855f7");
  if (tower.status === "demolished") return createTowerIcon("#64748b");
  return createTowerIcon("#f59e0b");
};

const FitBounds = ({ points, autoFit }) => {
  const map = useMap();
  const key = autoFit === "always"
    ? points.map((p) => `${p.latitude},${p.longitude}`).join("|")
    : autoFit ? "once" : "off";
  useEffect(() => {
    if (autoFit === "off" || autoFit === false) return;
    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 13);
    } else if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
};

const DepotPopup = ({ depot, onViewDetails }) => {
  const { has, add, remove } = useTripCart();
  const inTrip = has(depot.id);
  return (
    <div className="min-w-[220px] max-w-[280px]" data-testid={`popup-depot-${depot.id}`}>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-serif font-semibold text-sm leading-tight">{depot.name}</h3>
          <Badge
            variant={depot.status === "original" ? "default" : "secondary"}
            className="shrink-0 text-xs"
          >
            {depot.status}
          </Badge>
        </div>
        <div className="space-y-1 text-xs text-muted-foreground mb-3">
          <p className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {depot.city}, {depot.state}
          </p>
          {depot.railroad && (
            <p className="flex items-center gap-1">
              <Train className="w-3 h-3" />
              {depot.railroad}
            </p>
          )}
          {depot.year_built && <p className="font-mono">Built: {depot.year_built}</p>}
        </div>
        {depot.has_photo && (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <Camera className="w-3 h-3" />
            Photos available
          </span>
        )}
        <Button size="sm" className="w-full mt-3" onClick={() => onViewDetails(depot)}>
          View Details
          <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
        <Button
          size="sm"
          variant={inTrip ? "outline" : "secondary"}
          className={`w-full mt-2 ${inTrip ? "border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10" : ""}`}
          onClick={() => (inTrip ? remove(depot.id) : add(depot))}
          data-testid={`popup-add-trip-${depot.id}`}
        >
          {inTrip ? (
            <>
              <Check className="w-3 h-3 mr-1" /> In Trip · Remove
            </>
          ) : (
            <>
              <RouteIcon className="w-3 h-3 mr-1" /> Add to Trip
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const TowerPopup = ({ tower, onViewDetails }) => (
  <div className="min-w-[220px] max-w-[280px]" data-testid={`popup-tower-${tower.id}`}>
    <div className="p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-amber-500" />
          <h3 className="font-serif font-semibold text-sm leading-tight">
            {tower.call_letters && (
              <span className="font-mono text-amber-500 mr-1">{tower.call_letters}</span>
            )}
            {tower.name}
          </h3>
        </div>
        <Badge variant="secondary" className="shrink-0 text-xs capitalize">
          {tower.status}
        </Badge>
      </div>
      <div className="space-y-1 text-xs text-muted-foreground mb-3">
        <p className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {tower.city}, {tower.state}
        </p>
        {tower.railroad && (
          <p className="flex items-center gap-1">
            <Train className="w-3 h-3" />
            {tower.railroad}
          </p>
        )}
        {tower.tower_type && <p className="font-mono capitalize">Type: {tower.tower_type}</p>}
        {tower.year_built && <p className="font-mono">Built: {tower.year_built}</p>}
      </div>
      {tower.wikipedia_url && (
        <a
          href={tower.wikipedia_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-amber-600 hover:underline flex items-center gap-1 mb-2"
        >
          <ExternalLink className="w-3 h-3" />
          Wikipedia
        </a>
      )}
      <Button size="sm" className="w-full mt-2" onClick={() => onViewDetails(tower)}>
        View Details
        <ExternalLink className="w-3 h-3 ml-1" />
      </Button>
    </div>
  </div>
);

export const DepotMap = ({
  depots = [],
  towers = [],
  onDepotClick,
  onTowerClick,
  loading,
  center,
  zoom,
  darkMode = false,
  showDepots = true,
  showTowers = true,
  autoFit,
  routePolyline,
}) => {
  const defaultCenter = center || [39.8283, -98.5795];
  const defaultZoom = zoom || 4;

  const depotMarkers = useMemo(
    () => depots.map((d) => ({ ...d, icon: getDepotIcon(d) })),
    [depots]
  );
  const towerMarkers = useMemo(
    () => towers.map((t) => ({ ...t, icon: getTowerIcon(t) })),
    [towers]
  );

  // Tile provider — configurable via env for easy swap between CARTO / Esri /
  // Stadia / MapTiler etc. Default to OpenStreetMap Standard (free, no key,
  // no watermark, high contrast). A CSS filter tints it slightly for dark mode
  // so it feels at home inside the app's dark UI without becoming unreadable.
  const tileUrl = darkMode
    ? (process.env.REACT_APP_MAP_TILE_DARK_URL ||
        "https://tile.openstreetmap.org/{z}/{x}/{y}.png")
    : (process.env.REACT_APP_MAP_TILE_LIGHT_URL ||
        "https://tile.openstreetmap.org/{z}/{x}/{y}.png");
  const tileAttribution =
    process.env.REACT_APP_MAP_TILE_ATTRIBUTION ||
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  const tileLabelsUrl = null; // OSM standard has labels baked in
  const tileClassName = darkMode ? "leaflet-tile-dark" : "";

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#111]">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-white/50">Loading map...</p>
        </div>
      </div>
    );
  }

  const allPoints = [
    ...(showDepots ? depots : []),
    ...(showTowers ? towers : []),
  ];

  return (
    <MapContainer
      center={defaultCenter}
      zoom={defaultZoom}
      className="w-full h-full"
      scrollWheelZoom={true}
      data-testid="leaflet-map"
    >
      <TileLayer
        attribution={tileAttribution}
        url={tileUrl}
        className={tileClassName}
      />
      {tileLabelsUrl && (
        <TileLayer url={tileLabelsUrl} pane="overlayPane" />
      )}

      <FitBounds points={allPoints} autoFit={autoFit ?? (allPoints.length === 1)} />

      {routePolyline && routePolyline.length >= 2 && (
        <>
          {/* soft glow underlayer */}
          <Polyline
            positions={routePolyline}
            pathOptions={{
              color: "#f59e0b",
              weight: 10,
              opacity: 0.18,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
          {/* main amber route line */}
          <Polyline
            positions={routePolyline}
            pathOptions={{
              color: "#f59e0b",
              weight: 4,
              opacity: 0.95,
              dashArray: "1, 8",
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        </>
      )}

      {showDepots && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom
          disableClusteringAtZoom={10}
          iconCreateFunction={(cluster) => {
            const count = cluster.getChildCount();
            return L.divIcon({
              html: `<div style="background:rgba(34,197,94,0.85);color:#0a0a0a;font-weight:800;border:2px solid #fff;border-radius:9999px;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:12px;font-family:ui-monospace,monospace;box-shadow:0 0 0 4px rgba(34,197,94,0.15);">${count}</div>`,
              className: "depot-cluster",
              iconSize: L.point(36, 36, true),
            });
          }}
        >
          {depotMarkers.map((depot) => (
            <Marker
              key={`depot-${depot.id}`}
              position={[depot.latitude, depot.longitude]}
              icon={depot.icon}
            >
              <Popup>
                <DepotPopup depot={depot} onViewDetails={onDepotClick} />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      )}

      {showTowers && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom
          disableClusteringAtZoom={10}
          iconCreateFunction={(cluster) => {
            const count = cluster.getChildCount();
            return L.divIcon({
              html: `<div style="background:rgba(245,158,11,0.9);color:#0a0a0a;font-weight:800;border:2px solid #fff;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:11px;font-family:ui-monospace,monospace;transform:rotate(45deg);box-shadow:0 0 0 4px rgba(245,158,11,0.18);"><span style="transform:rotate(-45deg);">${count}T</span></div>`,
              className: "tower-cluster",
              iconSize: L.point(36, 36, true),
            });
          }}
        >
          {towerMarkers.map((tower) => (
            <Marker
              key={`tower-${tower.id}`}
              position={[tower.latitude, tower.longitude]}
              icon={tower.icon}
            >
              <Popup>
                <TowerPopup tower={tower} onViewDetails={onTowerClick} />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      )}
    </MapContainer>
  );
};

export default DepotMap;
